import React from 'react';
import { Segment } from 'semantic-ui-react';
import {
  CQV_VTME_CORRECTION_DISCLAIMER,
  CQV_VTME_QUEUE_SLA_DISCLAIMER,
  CqvVtmeAssignmentReportDto,
  CqvVtmePoolLoadDto,
  formatElapsedHours,
} from '../../services/cqv/cqvVtmeAssignmentService';
import { CQV_VTME_THEME, cqvVtmeQueueCardNameStyle, cqvVtmeQueueCardStyle } from './cqvVtmePageTheme';
import { formatCqvAnalystDisplayName } from './formatCqvAnalystDisplayName';
import CqvVtmeLinesSummary from './CqvVtmeLinesSummary';

type Props = {
  poolLoad: CqvVtmePoolLoadDto[];
  report?: CqvVtmeAssignmentReportDto | null;
  reportError?: boolean;
  onSelectAnalyst?: (userId: string) => void;
};

/** "…" = report ainda não chegou; "—" = report veio sem amostras / indisponível / falhou. */
const slaOrPending = (
  report: CqvVtmeAssignmentReportDto | null | undefined,
  unavailable: boolean,
  hours?: number | null
) => {
  if (!report) return '…';
  if (unavailable) return '—';
  return formatElapsedHours(hours);
};

const CqvVtmeQueueCards: React.FC<Props> = ({ poolLoad, report, reportError, onSelectAnalyst }) => {
  const kpis = report?.kpis;
  const vtmeUnavailable = kpis?.vtmeHistoryAvailable === false;
  const ageTo39Unavailable = (kpis?.ageTo39SampleCount ?? 0) === 0;
  const treatmentUnavailable = vtmeUnavailable || (kpis?.treatmentSampleCount ?? 0) === 0;
  const handoffUnavailable = vtmeUnavailable || (kpis?.handoffSampleCount ?? 0) === 0;
  const totalUnavailable = vtmeUnavailable || (kpis?.totalTreatmentSampleCount ?? 0) === 0;
  const slaSuffix = kpis?.periodIsToday === false ? ' do período' : '';
  const analystsById = new Map((report?.analysts || []).map(row => [row.userId, row]));
  const pendingSla = !report && !reportError;
  const failedSla = !report && !!reportError;

  // Totais do time no recorte, somando o que já vem por analista no poolLoad — mesma base de
  // linhas "processadas" (VTME criado de verdade) que os cards individuais mostram.
  // "Acesso" = só Aditivo (linha nova). Renegociação não é acesso novo, fica separado.
  const totalAcessos = poolLoad.reduce((sum, p) => sum + (p.linesAditivo ?? 0), 0);
  const totalRenegociacao = poolLoad.reduce((sum, p) => sum + (p.linesRenegociacao ?? 0), 0);
  const totalRenegociacaoApta = poolLoad.reduce((sum, p) => sum + (p.linesRenegociacaoApta ?? 0), 0);
  const totalRenegociacaoNaoApta = poolLoad.reduce((sum, p) => sum + (p.linesRenegociacaoNaoApta ?? 0), 0);

  return (
    <>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
          alignItems: 'stretch',
          gap: 12,
          marginBottom: 12,
        }}
      >
        <Segment style={cqvVtmeQueueCardStyle}>
          <div style={cqvVtmeQueueCardNameStyle}>SLA criação VTME{slaSuffix}</div>
          <div style={{ fontSize: 28, fontWeight: 800, lineHeight: 1, color: CQV_VTME_THEME.accentLilac }}>
            {failedSla ? '—' : slaOrPending(report, ageTo39Unavailable, kpis?.medianAgeTo39Hours)}
          </div>
          <div style={{ fontSize: 11, color: CQV_VTME_THEME.muted, marginTop: 2 }}>Aprovado CQV → VTME criado</div>
        </Segment>

        <Segment style={cqvVtmeQueueCardStyle}>
          <div style={cqvVtmeQueueCardNameStyle}>SLA tratamento VTME{slaSuffix}</div>
          <div style={{ fontSize: 28, fontWeight: 800, lineHeight: 1, color: CQV_VTME_THEME.accentLilac }}>
            {failedSla ? '—' : slaOrPending(report, treatmentUnavailable, kpis?.medianTreatmentHours)}
          </div>
          <div style={{ fontSize: 11, color: CQV_VTME_THEME.muted, marginTop: 2 }}>Qualidade/CVP → BackOffice</div>
          {kpis?.periodIsToday === false ? (
            <div style={{ marginTop: 8, color: CQV_VTME_THEME.text }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: CQV_VTME_THEME.muted }}>SLA de hoje</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>
                {failedSla ? '—' : slaOrPending(report, treatmentUnavailable, kpis?.medianTreatmentHoursToday)}
              </div>
            </div>
          ) : null}
        </Segment>

        <Segment style={cqvVtmeQueueCardStyle}>
          <div style={cqvVtmeQueueCardNameStyle}>SLA fluxo CQV{slaSuffix}</div>
          <div style={{ fontSize: 28, fontWeight: 800, lineHeight: 1, color: CQV_VTME_THEME.accentLilac }}>
            {failedSla ? '—' : slaOrPending(report, handoffUnavailable, kpis?.medianHandoffHours)}
          </div>
          <div style={{ fontSize: 11, color: CQV_VTME_THEME.muted, marginTop: 2 }}>VTME criado → Qualidade/CVP</div>
        </Segment>

        <Segment style={cqvVtmeQueueCardStyle}>
          <div style={cqvVtmeQueueCardNameStyle}>SLA total até BackOffice{slaSuffix}</div>
          <div style={{ fontSize: 28, fontWeight: 800, lineHeight: 1, color: CQV_VTME_THEME.accentLilac }}>
            {failedSla ? '—' : slaOrPending(report, totalUnavailable, kpis?.medianTotalTreatmentHours)}
          </div>
          <div style={{ fontSize: 11, color: CQV_VTME_THEME.muted, marginTop: 2 }}>Aprovado CQV → BackOffice</div>
        </Segment>

        <Segment style={cqvVtmeQueueCardStyle}>
          <div style={cqvVtmeQueueCardNameStyle}>
            {kpis?.periodIsToday === false ? 'Produção de hoje' : 'Produção do dia'}
          </div>
          <div style={{ fontSize: 28, fontWeight: 800, lineHeight: 1, color: CQV_VTME_THEME.accentLilac }}>
            {pendingSla ? '…' : failedSla ? '—' : (kpis?.productionTodayCount ?? 0)}
          </div>
          <div style={{ fontSize: 11, color: CQV_VTME_THEME.muted, marginTop: 8 }}>
            {pendingSla
              ? 'Carregando produção…'
              : failedSla
                ? 'Não foi possível carregar a produção'
                : `${kpis?.treatedCountToday ?? 0} tratados hoje + ${kpis?.correctionsClosedTodayCount ?? 0} corrigidos hoje`}
          </div>
          <div style={{ fontSize: 11, color: CQV_VTME_THEME.muted, marginTop: 6 }}>
            {kpis?.periodIsToday === false ? 'Linhas do período' : 'Linhas do dia'}
          </div>
          <div style={{ fontSize: 11, color: CQV_VTME_THEME.text, marginTop: 6, paddingTop: 6, borderTop: `1px dashed ${CQV_VTME_THEME.border}` }}>
            <b>{totalAcessos}</b> acessos (Aditivo)
          </div>
          <div style={{ fontSize: 11, color: CQV_VTME_THEME.muted, marginTop: 2 }}>
            <b style={{ color: CQV_VTME_THEME.text }}>{totalRenegociacao}</b> renegociação ·{' '}
            <b style={{ color: CQV_VTME_THEME.text }}>{totalRenegociacaoApta}</b> apta ·{' '}
            <b style={{ color: CQV_VTME_THEME.text }}>{totalRenegociacaoNaoApta}</b> não apta
          </div>
        </Segment>

        <Segment style={cqvVtmeQueueCardStyle}>
          <div style={cqvVtmeQueueCardNameStyle}>Em correção</div>
          <div style={{ fontSize: 28, fontWeight: 800, lineHeight: 1, color: CQV_VTME_THEME.accentLilac }}>
            {pendingSla ? '…' : failedSla ? '—' : (kpis?.inCorrectionCount ?? 0)}
          </div>
          <div style={{ fontSize: 11, color: CQV_VTME_THEME.muted, marginTop: 8 }}>
            {pendingSla
              ? 'Carregando…'
              : failedSla
                ? '—'
                : `${kpis?.correctionsClosedTodayCount ?? 0} corrigidos hoje`}
          </div>
        </Segment>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
          alignItems: 'stretch',
          gap: 12,
          marginBottom: 8,
        }}
      >
        {poolLoad.map(card => {
          const row = analystsById.get(card.userId);
          return (
            <Segment
              key={card.userId}
              style={{
                ...cqvVtmeQueueCardStyle,
                cursor: onSelectAnalyst ? 'pointer' : 'default',
              }}
              role={onSelectAnalyst ? 'button' : undefined}
              tabIndex={onSelectAnalyst ? 0 : undefined}
              onClick={() => onSelectAnalyst?.(card.userId)}
              onKeyDown={(event: React.KeyboardEvent) => {
                if (!onSelectAnalyst) return;
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onSelectAnalyst(card.userId);
                }
              }}
            >
              <div style={cqvVtmeQueueCardNameStyle}>{formatCqvAnalystDisplayName(card.name)}</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 12 }}>
                <div>
                  <div style={{ fontSize: 28, fontWeight: 800, lineHeight: 1, color: CQV_VTME_THEME.accentLilac }}>
                    {card.openCount ?? 0}
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: CQV_VTME_THEME.muted, marginTop: 4 }}>
                    Aguardando VTME
                  </div>
                </div>
                <div style={{ textAlign: 'right', color: CQV_VTME_THEME.text }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: CQV_VTME_THEME.accentLilac }}>
                    {card.completedCount ?? 0}
                  </div>
                  <div style={{ fontSize: 12, color: CQV_VTME_THEME.muted }}>VTME criado</div>
                </div>
              </div>
              <div style={{ marginTop: 10, color: CQV_VTME_THEME.text }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12 }}>
                  <span style={{ color: CQV_VTME_THEME.muted }}>Criação VTME</span>
                  <span style={{ fontWeight: 700 }}>
                    {failedSla ? '—' : slaOrPending(report, false, row?.medianAgeTo39HoursSla)}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12, marginTop: 2 }}>
                  <span style={{ color: CQV_VTME_THEME.muted }}>Tratamento VTME</span>
                  <span style={{ fontWeight: 700 }}>
                    {failedSla ? '—' : slaOrPending(report, vtmeUnavailable, row?.medianTreatmentHours)}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12, marginTop: 2 }}>
                  <span style={{ color: CQV_VTME_THEME.muted }}>VTME → Qualidade/CVP</span>
                  <span style={{ fontWeight: 700 }}>
                    {failedSla ? '—' : slaOrPending(report, vtmeUnavailable, row?.medianHandoffHours)}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12, marginTop: 2 }}>
                  <span style={{ color: CQV_VTME_THEME.muted }}>Total até BackOffice</span>
                  <span style={{ fontWeight: 700 }}>
                    {failedSla ? '—' : slaOrPending(report, vtmeUnavailable, row?.medianTotalTreatmentHours)}
                  </span>
                </div>
                {row?.aboveTeamMedianTreatment ? (
                  <div style={{ fontSize: 11, color: CQV_VTME_THEME.muted, marginTop: 2 }}>acima da mediana do time (tratamento)</div>
                ) : null}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10 }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: CQV_VTME_THEME.text }}>
                    {pendingSla ? '…' : failedSla ? '—' : (row?.inCorrectionCount ?? 0)}
                  </div>
                  <div style={{ fontSize: 11, color: CQV_VTME_THEME.muted }}>Em correção</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: CQV_VTME_THEME.text }}>
                    {pendingSla ? '…' : failedSla ? '—' : (row?.correctionsClosedTodayCount ?? 0)}
                  </div>
                  <div style={{ fontSize: 11, color: CQV_VTME_THEME.muted }}>Corrigidos hoje</div>
                </div>
              </div>
              <div style={{ marginTop: 10, paddingTop: 8, borderTop: `1px dashed ${CQV_VTME_THEME.border}` }}>
                <CqvVtmeLinesSummary
                  aditivo={card.linesAditivo}
                  renegociacao={card.linesRenegociacao}
                  apta={card.linesRenegociacaoApta}
                  naoApta={card.linesRenegociacaoNaoApta}
                  semMarcacao={card.linesRenegociacaoSemMarcacao}
                />
              </div>
            </Segment>
          );
        })}
      </div>
      <p style={{ fontSize: 12, color: CQV_VTME_THEME.muted, margin: '0 0 16px' }}>
        {failedSla
          ? 'SLA e produção do topo vêm do relatório; a fila (Aguardando/VTME criado) já veio da lista. Tente filtrar de novo.'
          : pendingSla
            ? 'Aguardando o relatório de SLA… A fila já está atualizada.'
            : `${CQV_VTME_QUEUE_SLA_DISCLAIMER} ${CQV_VTME_CORRECTION_DISCLAIMER}`}
      </p>
    </>
  );
};

export default CqvVtmeQueueCards;
